import React, { useEffect } from 'react';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import { MapContainer, TileLayer, CircleMarker, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet.markercluster';

function ClusterGroup({ markers }) {
  const map = useMap();
  useEffect(() => {
    const group = L.markerClusterGroup({ chunkedLoading: true, maxClusterRadius: 50 });
    markers.forEach((m) => {
      const marker = L.marker([m.lat, m.lng]);
      const text = `${m.label || ''}${m.count ? ` — ${m.count}` : ''}`;
      if (text.trim()) marker.bindPopup(text);
      group.addLayer(marker);
    });
    map.addLayer(group);
    return () => { map.removeLayer(group); };
  }, [map, markers]);
  return null;
}

// Bundlers break Leaflet's default marker image paths — point them at the CDN.
const DefaultIcon = L.icon({
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41],
});
L.Marker.prototype.options.icon = DefaultIcon;

// Reusable OpenStreetMap map (no API key needed).
// markers: [{ lat, lng, label, count }]
// singlePin: render classic pin markers (for addresses); otherwise scaled circles.
export default function LeafletMap({ markers = [], center, zoom = 2, height = 380, singlePin = false, cluster = false, scrollWheelZoom = false, testid }) {
  const pts = markers.filter((m) => typeof m.lat === 'number' && typeof m.lng === 'number');
  const c = center || (pts.length ? [pts[0].lat, pts[0].lng] : [20, 0]);
  const maxCount = Math.max(1, ...pts.map((p) => p.count || 1));

  return (
    <div style={{ height }} data-testid={testid} className="rounded-lg overflow-hidden border" >
      <MapContainer center={c} zoom={zoom} style={{ height: '100%', width: '100%' }} scrollWheelZoom={scrollWheelZoom}>
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {cluster ? (
          <ClusterGroup markers={pts} />
        ) : singlePin
          ? pts.map((m, i) => (
              <Marker key={i} position={[m.lat, m.lng]}>
                {m.label && <Popup>{m.label}</Popup>}
              </Marker>
            ))
          : pts.map((m, i) => {
              const radius = 6 + 16 * Math.sqrt((m.count || 1) / maxCount);
              return (
                <CircleMarker
                  key={i}
                  center={[m.lat, m.lng]}
                  radius={radius}
                  pathOptions={{ color: '#8A0A06', weight: 1, fillColor: '#E7B84B', fillOpacity: 0.45 }}
                >
                  {(m.label || m.count) && (
                    <Popup>{m.label}{m.count ? ` — ${m.count}` : ''}</Popup>
                  )}
                </CircleMarker>
              );
            })}
      </MapContainer>
    </div>
  );
}
